import { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'

import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyBurnInfoModal from 'loyalty/components/LoyaltyBurnInfoModal'
import { InfoIconButton, SmallSwitch } from 'loyalty/components/LoyaltyBurnSliderPrimitives'
import { CalendarBurnContext } from './CalendarBurnContext'
import useCalendarBurnToggleVisible from './useCalendarBurnToggleVisible'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  min-width: 0;
`

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  min-width: 0;
`

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

function OfferCalendarBurnToggle() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const [tier] = useCurrentLoyaltyTier()
  const { enabled: isEnabled, setEnabled } = useContext(CalendarBurnContext)
  const shouldShow = useCalendarBurnToggleVisible()

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)

  const handleToggle = useCallback(async() => {
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    setEnabled(!isEnabled)
  }, [isLoggedIn, isEnabled, setEnabled, showModal])

  const handleInfoClick = useCallback(() => {
    showModal(<LoyaltyBurnInfoModal />)
  }, [showModal])

  if (!shouldShow) {
    return null
  }

  return (
    <Container>
      <Left>
        <LoyaltyTierIcon tier={tier ?? 'gold'} size="M" />
        <TextGroup>
          <HeadingRow>
            <BodyText variant="medium" weight="bold">
              <FormattedMessage
                id="arrY3EIONTYI7vlgVITc"
                defaultMessage="Use {programName} points to pay"
                
                values={{ programName: getLoyaltyProgramDisplayName() }}
              />
            </BodyText>
            <InfoIconButton
              onClick={handleInfoClick}
              aria-label={intl.formatMessage(
                {
                  id: 'tB8M5ogsc_sJY6Y3f75S',
                  defaultMessage: 'Learn more about {programName} points',
                },
                { programName: getLoyaltyProgramDisplayName() },
              )}
            >
              <LineInfoCircleIcon size="XS" />
            </InfoIconButton>
          </HeadingRow>
          {hasLoyaltyAccount && <Caption variant="medium" colour="normal-contrast">
            <FormattedMessage
              id="IybuZyVzgiAs9iGI9VOB"
              defaultMessage="Your balance: {points} pts"
              
              values={{ points: intl.formatNumber(userPoints) }}
            />
          </Caption>}
        </TextGroup>
      </Left>
      <SmallSwitch
        checked={isEnabled}
        onChange={handleToggle}
        aria-label={intl.formatMessage({
          id: 'CHTRwDYdC64aqr2BgjNY',
          defaultMessage: 'Toggle points usage',
        })}
      />
    </Container>
  )
}

export default OfferCalendarBurnToggle
